import type { FC, ReactNode } from 'react'
import type { NavGroup } from '@/content/navigation'

import Link from 'next/link'
import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import {
    ArrowRight,
    ChevronDown,
    Menu,
    Phone,
    Search
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { WIDE_CONTAINER } from '@/lib/layout'
import OpenStatus from '@/components/generals/open-status'
import TextSizeToggle from '@/components/generals/text-size-toggle'
import MobileMenu from '@/components/generals/mobile-menu'
import SearchDialog from '@/components/search/search-dialog'
import WindowDiagram from '@/components/sections/window-diagram'

import { BUSINESS } from '@/content/business'
import { getImage } from '@/content/images'
import { MAIN_NAV, SIMPLE_NAV } from '@/content/navigation'

const isActive = (path: string, href: string): boolean => {
    if (href === '/') return path === '/'

    return path === href || path.startsWith(href)
}

const groupActive = (path: string, group: NavGroup): boolean => isActive(path, group.href)
    || group.columns.some(column => column.links.some(link => link.href !== '/service-areas/' && isActive(path, link.href)))

interface MegaPanelProps {
    group: NavGroup
    path: string
    onNavigate: () => void
}

const MegaPanel: FC<MegaPanelProps> = ({ group, path, onNavigate }): ReactNode => {
    const image = getImage(group.feature.image)
    const wide = group.columns.length >= 3

    return (
        <div className='mega-panel absolute inset-x-0 top-full z-50 max-h-[calc(100svh-4rem)] overflow-y-auto border-t border-white/10 bg-white text-foreground shadow-[0_30px_60px_-20px_rgba(10,26,43,0.45)]'>
            <div className={cn('mx-auto grid max-w-7xl gap-8 px-8 py-6', wide ? 'grid-cols-[16rem_minmax(0,1fr)]' : 'grid-cols-[19rem_minmax(0,1fr)]')}>
                <Link href={group.feature.href} onClick={onNavigate} className='group relative flex min-h-[16rem] flex-col justify-end self-start overflow-hidden rounded-2xl bg-navy-deep text-white'>
                    <Image
                        src={image.src}
                        alt=''
                        fill
                        sizes='22rem'
                        className='object-cover opacity-80 transition-transform duration-700 group-hover:scale-105'
                    />

                    <span aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-[#0a1016] via-[#0a1016]/70 to-transparent' />

                    <span className='relative p-5'>
                        <span className='block font-heading text-xl font-bold leading-tight text-white'>
                            {group.feature.title}
                        </span>

                        <span className='mt-1.5 block text-sm leading-snug text-white/90'>
                            {group.feature.text}
                        </span>

                        <span className='mt-3 inline-flex items-center gap-2 text-sm font-bold text-sky'>
                            {group.feature.cta}
                            <ArrowRight className='size-5 transition-transform group-hover:translate-x-1' aria-hidden='true' />
                        </span>
                    </span>
                </Link>

                <div>
                    {group.note && (
                        <p className='mb-4 rounded-lg bg-[#fff4d6] px-3.5 py-2 text-sm font-bold text-navy'>
                            {group.note}
                        </p>
                    )}

                    <div className={cn('grid gap-x-6 gap-y-4', group.columns.length >= 3 ? 'grid-cols-3' : group.columns.length === 2 ? 'grid-cols-2' : 'grid-cols-1')}>
                        {group.columns.map((column, columnIndex) => (
                            <div key={column.title} className='mega-panel' style={{ animationDelay: `${60 + columnIndex * 60}ms` }}>
                                <p className='border-b border-border pb-1.5 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground'>
                                    {column.title}
                                </p>

                                <ul className={cn('mt-2 grid gap-0.5', column.links.some(link => link.diagram) && column.links.length > 4 && 'grid-cols-2 gap-x-2', group.columns.length === 1 && column.links.length > 6 && 'grid-cols-2 gap-x-6', group.columns.length === 1 && column.links.length > 8 && 'grid-cols-3')}>
                                    {column.links.map(link => (
                                        <li key={link.href}>
                                            <Link
                                                href={link.href}
                                                onClick={onNavigate}
                                                aria-current={path === link.href ? 'page' : undefined}
                                                className='group flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-secondary'
                                            >
                                                {link.diagram && (
                                                    <span className='flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-white p-0.5'>
                                                        <WindowDiagram diagram={link.diagram} />
                                                    </span>
                                                )}

                                                <span>
                                                    <span className='block text-base font-bold leading-snug text-navy group-hover:text-primary'>
                                                        {link.label}
                                                    </span>

                                                    {link.description && (
                                                        <span className='block text-sm leading-snug text-muted-foreground'>
                                                            {link.description}
                                                        </span>
                                                    )}
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

// Phones and tablets: one slim sticky bar (logo, search, menu; estimate and call
// icon once there is room). Wide screens (1400px, written in rem so it orders
// correctly against the named breakpoints): a brand row that scrolls away, then
// a sticky menu row with every section, which picks up its own estimate button
// once the brand row has gone.
const NAV_ITEM = 'flex min-h-14 items-center gap-1 whitespace-nowrap border-b-[3px] px-2.5 text-[1.0625rem] font-semibold text-white transition-colors hover:border-[#3b5266] min-[96rem]:px-3.5'

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const brandRef = useRef<HTMLElement>(null)
    const navRef = useRef<HTMLDivElement>(null)
    const openTimer = useRef<number | undefined>(undefined)
    const closeTimer = useRef<number | undefined>(undefined)
    const [openGroup, setOpenGroup] = useState<string | null>(null)
    const [mobileOpen, setMobileOpen] = useState(false)
    const [searchOpen, setSearchOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [stuck, setStuck] = useState(false)
    const path = router.asPath.split(/[?#]/)[0]

    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY

            setScrolled(y > 8)
            setStuck(y > (brandRef.current?.offsetHeight ?? 88))
        }

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => {
            setOpenGroup(null)
            setMobileOpen(false)
            setSearchOpen(false)
        }

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!openGroup) return

        const onPointer = (event: MouseEvent) => {
            if (navRef.current && !navRef.current.contains(event.target as Node)) setOpenGroup(null)
        }

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpenGroup(null)
        }

        document.addEventListener('mousedown', onPointer)
        document.addEventListener('keydown', onKey)

        return () => {
            document.removeEventListener('mousedown', onPointer)
            document.removeEventListener('keydown', onKey)
        }
    }, [openGroup])

    useEffect(() => () => {
        window.clearTimeout(openTimer.current)
        window.clearTimeout(closeTimer.current)
    }, [])

    // Hover opens after a short pause so a passing mouse never flips menus open.
    const hoverOpen = (label: string) => {
        window.clearTimeout(closeTimer.current)
        window.clearTimeout(openTimer.current)
        openTimer.current = window.setTimeout(() => setOpenGroup(label), openGroup ? 0 : 140)
    }

    const hoverClose = () => {
        window.clearTimeout(openTimer.current)
        closeTimer.current = window.setTimeout(() => setOpenGroup(null), 260)
    }

    const closeAll = useCallback(() => setOpenGroup(null), [])
    const closeSearch = useCallback(() => setSearchOpen(false), [])
    const closeMobile = useCallback(() => setMobileOpen(false), [])

    const activeGroup = MAIN_NAV.find(group => group.label === openGroup)
    const shadow = 'shadow-[0_12px_30px_-12px_rgba(0,0,0,0.55)]'

    return (
        <>
            <header
                ref={brandRef}
                className={cn(
                    'sticky top-0 z-50 bg-[#101820] text-white transition-shadow duration-300 min-[87.5rem]:static',
                    scrolled && cn(shadow, 'min-[87.5rem]:shadow-none')
                )}
            >
                <div className={cn(WIDE_CONTAINER, 'flex min-h-16 items-center gap-3 py-2 sm:gap-4 lg:min-h-[5.5rem] lg:py-3')}>
                    <Link href='/' className='flex shrink-0 items-center' aria-label='The ACM Pros home'>
                        <Image
                            src='/images/acm/logo-dark.webp'
                            alt='The ACM Pros Exteriors'
                            width={194}
                            height={128}
                            priority
                            className='h-12 w-auto sm:h-14 lg:h-16'
                        />
                    </Link>

                    <div className='hidden min-w-0 border-l border-white/15 pl-5 text-sm leading-snug text-[#c9d6e0] lg:block'>
                        <p className='hidden font-semibold text-white xl:block'>
                            Licensed and insured. {BUSINESS.licenseLabel}
                        </p>

                        <OpenStatus className='mt-0.5 block' />
                    </div>

                    <div className='ml-auto flex items-center gap-2 sm:gap-3'>
                        <TextSizeToggle className='mr-2 hidden min-[87.5rem]:flex' />

                        <button
                            type='button'
                            onClick={() => setSearchOpen(true)}
                            aria-label='Search the site'
                            className='inline-flex min-h-12 min-w-12 items-center justify-center gap-2 rounded-lg border-2 border-white/25 px-3 font-bold text-white transition-colors hover:border-white min-[87.5rem]:hidden'
                        >
                            <Search className='size-5' aria-hidden='true' />
                            <span className='hidden sm:inline'>Search</span>
                        </button>

                        <a
                            href={BUSINESS.phoneHref}
                            aria-label={`Call ${BUSINESS.phone}`}
                            title={`Call ${BUSINESS.phone}`}
                            className='hidden size-12 shrink-0 items-center justify-center rounded-lg border-2 border-white/25 text-white transition-colors hover:border-white hover:bg-white/10 md:inline-flex min-[87.5rem]:hidden'
                        >
                            <Phone className='size-5 text-sky' aria-hidden='true' />
                        </a>

                        <a
                            href={BUSINESS.phoneHref}
                            className='hidden items-center gap-2 whitespace-nowrap px-2 text-lg font-bold text-white underline-offset-4 hover:underline min-[87.5rem]:inline-flex'
                        >
                            <Phone className='size-5 text-sky' aria-hidden='true' />
                            {BUSINESS.phone}
                        </a>

                        <Link
                            href='/contact/'
                            className='hidden min-h-12 items-center whitespace-nowrap rounded-lg bg-[#0074c8] px-5 font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#0066b0] sm:inline-flex'
                        >
                            Free estimate
                        </Link>

                        <button
                            type='button'
                            onClick={() => setMobileOpen(true)}
                            aria-expanded={mobileOpen}
                            aria-controls='mobile-menu'
                            className='inline-flex min-h-12 items-center gap-2 rounded-lg border-2 border-white/25 px-3.5 font-bold text-white transition-colors hover:border-white min-[87.5rem]:hidden'
                        >
                            <Menu className='size-5' aria-hidden='true' />
                            <span>Menu</span>
                        </button>
                    </div>
                </div>
            </header>

            <div
                ref={navRef}
                onMouseLeave={hoverClose}
                onMouseEnter={() => window.clearTimeout(closeTimer.current)}
                className={cn(
                    'sticky top-0 z-50 hidden border-t border-white/10 bg-[#101820] text-white transition-shadow duration-300 min-[87.5rem]:block',
                    stuck && shadow
                )}
            >
                <div className={cn(WIDE_CONTAINER, 'flex items-center justify-between gap-6')}>
                    <nav aria-label='Main'>
                        <ul className='-ml-2.5 flex items-stretch min-[96rem]:-ml-3.5'>
                            {MAIN_NAV.map(group => {
                                const expanded = openGroup === group.label
                                const active = groupActive(path, group)

                                return (
                                    <li key={group.label} onMouseEnter={() => hoverOpen(group.label)}>
                                        <button
                                            type='button'
                                            aria-expanded={expanded}
                                            aria-controls='mega-menu'
                                            onClick={() => setOpenGroup(expanded ? null : group.label)}
                                            className={cn(NAV_ITEM, expanded || active ? 'border-[#188bf6]' : 'border-transparent')}
                                        >
                                            {group.label}
                                            <ChevronDown className={cn('size-4 transition-transform duration-200', expanded && 'rotate-180')} aria-hidden='true' />
                                        </button>
                                    </li>
                                )
                            })}

                            {SIMPLE_NAV.map(link => (
                                <li key={link.href} onMouseEnter={hoverClose}>
                                    <Link
                                        href={link.href}
                                        aria-current={isActive(path, link.href) ? 'page' : undefined}
                                        className={cn(NAV_ITEM, isActive(path, link.href) ? 'border-[#188bf6]' : 'border-transparent')}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex shrink-0 items-center gap-3'>
                        <button
                            type='button'
                            onClick={() => setSearchOpen(true)}
                            aria-label='Search the site'
                            className='inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-lg border-2 border-white/20 px-3 font-bold text-white transition-colors hover:border-white'
                        >
                            <Search className='size-5' aria-hidden='true' />
                            {!stuck && <span>Search</span>}
                        </button>

                        {stuck && (
                            <>
                                <a
                                    href={BUSINESS.phoneHref}
                                    aria-label={`Call ${BUSINESS.phone}`}
                                    title={`Call ${BUSINESS.phone}`}
                                    className='hidden size-11 items-center justify-center rounded-lg border-2 border-white/20 text-white transition-colors animate-in fade-in hover:border-white min-[96rem]:inline-flex'
                                >
                                    <Phone className='size-5 text-sky' aria-hidden='true' />
                                </a>

                                <Link
                                    href='/contact/'
                                    className='inline-flex min-h-11 items-center whitespace-nowrap rounded-lg bg-[#0074c8] px-4 font-bold text-white shadow-sm transition-colors animate-in fade-in slide-in-from-right-2 duration-300 hover:bg-[#0066b0]'
                                >
                                    Free estimate
                                </Link>
                            </>
                        )}
                    </div>
                </div>

                {activeGroup && (
                    <div id='mega-menu'>
                        <MegaPanel key={activeGroup.label} group={activeGroup} path={path} onNavigate={closeAll} />
                    </div>
                )}
            </div>

            <MobileMenu open={mobileOpen} onClose={closeMobile} onSearch={() => {
                setMobileOpen(false)
                setSearchOpen(true)
            }} />

            <SearchDialog open={searchOpen} onClose={closeSearch} />
        </>
    )
}

export default SiteHeader
